import * as React from 'react';
import { z } from 'zod';
import { Button } from './Button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './Dialog';
import { Form, FormField, useZodForm } from './Form';
import { Textarea } from './Input';
import { Inline } from './Layout';
import { ReasonPicker, type ReasonPickerOption } from './Picker';

export interface UrgentlyCancelModalProps {
  isOpen: boolean;
  /** The chosen reason and, when given, the trimmed notes. */
  onSubmit: (cancelReason: string, notes?: string) => void;
  onCancel: () => void;
  /** The cancellation is in flight: the controls lock and the button says so. */
  isSubmitting: boolean;
}

/** Urgently's cancellation reasons, as the partner API names them. */
const URGENTLY_CANCEL_REASONS: ReasonPickerOption[] = [
  { value: 'CUSTOMER_CANCELLED', label: 'Customer Cancelled' },
  { value: 'REQUIREMENT_CHANGED', label: 'Requirement Changed' },
  { value: 'DIVERTED_TO_OTHER_JOB', label: 'Diverted to Other Job' },
  { value: 'SPECIALIST_RESOLVED', label: 'Specialist Resolved' },
  { value: 'OTHER', label: 'Other' },
];

/**
 * Cancelling an Urgently job: a reason from the partner's list, notes that
 * become required when the reason is "Other", and a destructive confirm.
 *
 *   <UrgentlyCancelModal isOpen={open} onSubmit={cancelJob}
 *     onCancel={close} isSubmitting={saving} />
 *
 * The form state is the dialog's own — it resets on cancel — so the screen
 * only sees the finished choice.
 */
const FORM_ID = 'urgently-cancel-form';

/** Notes are required only when the reason is "Other". */
export const urgentlyCancelSchema = z
  .object({
    cancelReason: z.string().min(1, 'Please select a cancellation reason'),
    notes: z.string(),
  })
  .refine((v) => v.cancelReason !== 'OTHER' || v.notes.trim().length > 0, {
    path: ['notes'],
    message: 'Notes are required when selecting "Other"',
  });

export function UrgentlyCancelModal({
  isOpen,
  onSubmit,
  onCancel,
  isSubmitting,
}: UrgentlyCancelModalProps) {
  const [reasonOpen, setReasonOpen] = React.useState(false);
  const form = useZodForm(urgentlyCancelSchema, {
    defaultValues: { cancelReason: '', notes: '' },
  });

  const handleCancel = () => {
    form.reset({ cancelReason: '', notes: '' });
    setReasonOpen(false);
    onCancel();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleCancel}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cancel Job</DialogTitle>
        </DialogHeader>

        <Form
          form={form}
          gap={4}
          id={FORM_ID}
          onSubmit={(values) =>
            onSubmit(values.cancelReason, values.notes.trim() || undefined)
          }
        >
          <FormField
            form={form}
            name="cancelReason"
            label="Cancellation Reason"
          >
            {(field) => (
              <ReasonPicker
                options={URGENTLY_CANCEL_REASONS}
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value);
                  setReasonOpen(false);
                }}
                open={reasonOpen}
                onOpenChange={setReasonOpen}
                disabled={isSubmitting}
                maxHeight="sm"
              />
            )}
          </FormField>

          {/* Required only for "Other", which the schema decides and the
              marker follows — the two used to be set apart from each other. */}
          <FormField
            form={form}
            name="notes"
            label="Notes"
            required={form.watch('cancelReason') === 'OTHER'}
          >
            {(field) => (
              <Textarea
                {...field}
                disabled={isSubmitting}
                placeholder="Enter any additional notes or details..."
                rows={3}
              />
            )}
          </FormField>
        </Form>

        <DialogFooter>
          <Inline grow justify="between">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              form={FORM_ID}
              type="submit"
              disabled={isSubmitting}
              variant="destructive"
            >
              {isSubmitting ? 'Cancelling...' : 'Cancel Job'}
            </Button>
          </Inline>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
